import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  Building2,
  Users,
  Baby,
  FileText,
  TrendingUp,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowUpRight,
  ShieldCheck,
  Activity,
  Euro,
  UserCheck
} from 'lucide-react'

// ============================================
// KPI CARD
// ============================================
const KpiCard = ({ label, value, icon: Icon, color, subtitle }) => {
  const colorStyles = {
    blue: 'border-l-blue-500 dark:from-violet-600 dark:to-violet-700',
    emerald: 'border-l-emerald-500 dark:from-emerald-600 dark:to-emerald-700',
    violet: 'border-l-violet-500 dark:from-fuchsia-600 dark:to-fuchsia-700',
    amber: 'border-l-amber-500 dark:from-amber-600 dark:to-amber-700'
  }

  const iconBgStyles = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-violet-500/20 dark:text-violet-300',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300',
    violet: 'bg-violet-100 text-violet-600 dark:bg-fuchsia-500/20 dark:text-fuchsia-300',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300'
  }

  return (
    <div className={`
      bg-white rounded-lg shadow-sm border-l-4 p-4
      dark:bg-gradient-to-br dark:border-l-0 dark:rounded-2xl dark:border dark:border-zinc-800
      ${colorStyles[color]}
    `}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-500 dark:text-zinc-400 text-sm">{label}</p>
          <p className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${iconBgStyles[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  )
}

// ============================================
// ALERT CARD
// ============================================
const AlertCard = ({ icon: Icon, title, subtitle, value, variant = 'warning', actionLabel, onAction }) => {
  const styles = {
    warning: {
      container: 'bg-amber-500/10 border-amber-500/20 dark:bg-amber-500/10 dark:border-amber-500/20',
      iconBg: 'bg-amber-500/20 dark:bg-amber-500/20',
      icon: 'text-amber-600 dark:text-amber-400',
      title: 'text-amber-800 dark:text-amber-200',
      subtitle: 'text-amber-600 dark:text-amber-400/70',
      value: 'text-amber-700 dark:text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400'
    },
    danger: {
      container: 'bg-red-500/10 border-red-500/20 dark:bg-red-500/10 dark:border-red-500/20',
      iconBg: 'bg-red-500/20 dark:bg-red-500/20',
      icon: 'text-red-600 dark:text-red-400',
      title: 'text-red-800 dark:text-red-200',
      subtitle: 'text-red-600 dark:text-red-400/70',
      value: 'text-red-700 dark:text-red-400',
      button: 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400'
    }
  }

  const s = styles[variant]

  return (
    <div className={`rounded-xl border p-4 flex items-center justify-between ${s.container}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.iconBg}`}>
          <Icon className={`h-5 w-5 ${s.icon}`} />
        </div>
        <div>
          <p className={`font-medium ${s.title}`}>{title}</p>
          <p className={`text-sm ${s.subtitle}`}>{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className={`text-3xl font-bold ${s.value}`}>{value}</span>
        {actionLabel && onAction && (
          <button 
            onClick={onAction}
            className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors ${s.button}`}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================
// STATUS BADGE
// ============================================
const StatusBadge = ({ status }) => {
  const styles = {
    trial: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
    suspended: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
    deleted: 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400'
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.trial}`}>
      {status}
    </span>
  )
}

// ============================================
// BAR CHART
// ============================================
const BarChart = ({ data, labelKey, valueKey }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0), 1)

  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={idx}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600 dark:text-zinc-400">{item[labelKey]}</span>
            <span className="text-slate-800 dark:text-white font-medium">{item[valueKey]}</span>
          </div>
          <div className="h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 dark:bg-gradient-to-r dark:from-violet-500 dark:to-fuchsia-500 rounded-full transition-all duration-500"
              style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================
// MAIN DASHBOARD
// ============================================
export default function DashboardPage() {
  const [data, setData] = useState(null)
  const [systemStatus, setSystemStatus] = useState(null)
  const [advancedStats, setAdvancedStats] = useState(null)
  const [nonCompliantCount, setNonCompliantCount] = useState(0)
  const [readOnlyCount, setReadOnlyCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [dashboardRes, statusRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getSystemStatus()
      ])
      setData(dashboardRes.data.data)
      setSystemStatus(statusRes.data.data.status)

      // Carica statistiche avanzate
      try {
        const statsRes = await adminApi.getDashboardStats()
        setAdvancedStats(statsRes.data.data)
      } catch (e) {
        console.log('Stats avanzate non disponibili:', e.message)
      }

      // Conta organization non conformi (silent fail)
      try {
        const compRes = await adminApi.getNonCompliantOrganizations({ countOnly: true })
        setNonCompliantCount(compRes.data?.data?.count || 0)
        setReadOnlyCount(compRes.data?.data?.readOnlyCount || 0)
      } catch {
        setNonCompliantCount(0)
        setReadOnlyCount(0)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-violet-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-4 text-red-700 dark:text-red-400">
        {error}
      </div>
    )
  }

  // Prepara dati per grafici
  const planData = Object.entries(data?.tenants?.byPlan || {}).map(([plan, count]) => ({
    name: plan.charAt(0).toUpperCase() + plan.slice(1),
    value: count
  }))

  const statusData = Object.entries(data?.tenants?.byStatus || {}).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count
  }))

  return (
    <div className="space-y-6">
      {/* Header con Status Sistema */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Dashboard</h1>
          <p className="text-slate-500 dark:text-zinc-500">Panoramica del sistema</p>
        </div>
        
        {/* System Status - Compatto */}
        <div className="flex items-center gap-3 bg-white dark:bg-zinc-900 rounded-lg dark:rounded-full px-4 py-2 shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              {systemStatus?.database === 'ok' ? (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              <span className="text-slate-600 dark:text-zinc-400">DB</span>
            </div>
            <div className="flex items-center gap-1.5">
              {systemStatus?.stripe === 'configured' ? (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
              <span className="text-slate-600 dark:text-zinc-400">Stripe</span>
            </div>
            <div className="flex items-center gap-1.5">
              {systemStatus?.email === 'configured' ? (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
              <span className="text-slate-600 dark:text-zinc-400">Email</span>
            </div>
          </div>
          <span className="text-xs text-slate-400 dark:text-zinc-600 border-l border-slate-200 dark:border-zinc-700 pl-3">
            {systemStatus?.environment}
          </span>
        </div>
      </div>

      {/* Banner Compliance Alert */}
      {nonCompliantCount > 0 && (
        <Link
          to="/legal-documents"
          className="block bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-red-800 dark:text-red-200">
                {readOnlyCount > 0 ? (
                  <>{readOnlyCount} organization in sola-lettura</>
                ) : (
                  <>{nonCompliantCount} organization con documenti scaduti</>
                )}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400/70">
                {nonCompliantCount > readOnlyCount && (
                  <>{nonCompliantCount} totali · </>
                )}
                Documenti legali required non firmati. Vai alla lista per gestire reminder, estensioni o esoneri.
              </p>
            </div>
            <ArrowUpRight className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          </div>
        </Link>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Centri Registrati"
          value={data?.overview.totalTenants || 0}
          icon={Building2}
          color="blue"
        />
        <KpiCard
          label="Revenue Mensile"
          value={`€${Math.round((data?.revenue.estimatedMonthly || 0) / 100)}`}
          icon={Euro}
          color="emerald"
          subtitle={data?.revenue.activeSubscriptions > 0 ? `${data.revenue.activeSubscriptions} abbonamenti` : null}
        />
        <KpiCard
          label="Abbonamenti Attivi"
          value={data?.revenue.activeSubscriptions || 0}
          icon={CheckCircle}
          color="violet"
        />
        <KpiCard
          label="Utenti Attivi"
          value={advancedStats?.activeUsers ?? 0}
          icon={UserCheck}
          color="amber"
          subtitle="ultimi 7 giorni"
        />
      </div>

      {/* Alert Section */}
      {(data?.tenants.expiringTrials > 0 || (advancedStats?.failedLogins || 0) > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data?.tenants.expiringTrials > 0 && (
            <AlertCard
              icon={Clock}
              title="Trial in scadenza"
              subtitle="Prossimi 7 giorni"
              value={data.tenants.expiringTrials}
              variant="warning"
            />
          )}
          {(advancedStats?.failedLogins || 0) > 0 && (
            <AlertCard
              icon={ShieldCheck}
              title="Login falliti"
              subtitle="Ultimi 7 giorni"
              value={advancedStats?.failedLogins || 0}
              variant="danger"
            />
          )}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribuzione Piani */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-2xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 dark:text-white">Distribuzione Piani</h3>
            <Link 
              to="/plans" 
              className="text-blue-600 dark:text-violet-400 text-sm hover:underline flex items-center gap-1"
            >
              Gestisci <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {planData.length > 0 ? (
            <BarChart data={planData} labelKey="name" valueKey="value" />
          ) : (
            <p className="text-slate-500 dark:text-zinc-500 text-center py-4">Nessun dato</p>
          )}
        </div>

        {/* Ultime Registrazioni */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-2xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 dark:text-white">Ultime Registrazioni</h3>
            <Link 
              to="/tenants" 
              className="text-blue-600 dark:text-violet-400 text-sm hover:underline flex items-center gap-1"
            >
              Vedi tutti <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          
          {/* Light Mode: Table */}
          <div className="hidden dark:hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                  <th className="pb-2 font-medium">Centro</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Data</th>
                  <th className="pb-2 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {data?.latestTenants?.map((tenant) => (
                  <tr key={tenant._id} className="border-b border-slate-100 last:border-0">
                    <td className="py-3">
                      <Link 
                        to={`/tenants/${tenant._id}`}
                        className="text-slate-800 font-medium hover:text-blue-600"
                      >
                        {tenant.name}
                      </Link>
                    </td>
                    <td className="py-3 text-slate-500">{tenant.contacts?.email}</td>
                    <td className="py-3 text-slate-500">
                      {new Date(tenant.registeredAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="py-3">
                      <StatusBadge status={tenant.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Dark Mode: Cards */}
          <div className="md:hidden dark:block space-y-3">
            {data?.latestTenants?.map((tenant) => (
              <Link
                key={tenant._id}
                to={`/tenants/${tenant._id}`}
                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-gradient-to-br dark:from-violet-500 dark:to-fuchsia-500 flex items-center justify-center text-blue-600 dark:text-white font-bold">
                    {tenant.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-slate-800 dark:text-white font-medium">{tenant.name}</p>
                    <p className="text-slate-500 dark:text-zinc-500 text-sm">{tenant.contacts?.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={tenant.status} />
                  <p className="text-slate-400 dark:text-zinc-600 text-xs mt-1">
                    {new Date(tenant.registeredAt).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Centri per Stato */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-2xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-5">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-4">Centri per Stato</h3>
          {statusData.length > 0 ? (
            <BarChart data={statusData} labelKey="name" valueKey="value" />
          ) : (
            <p className="text-slate-500 dark:text-zinc-500 text-center py-4">Nessun dato</p>
          )}
        </div>

        {/* Statistiche Rapide */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-2xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-5">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-4">Statistiche</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-800 dark:text-white">
                {data?.overview.totalMinori || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Minori totali</p>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-800 dark:text-white">
                {data?.overview.totalEntries || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Registrazioni</p>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-800 dark:text-white">
                {data?.tenants.recentRegistrations || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Nuovi (30gg)</p>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-800 dark:text-white">
                {data?.overview.activeCoupons || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Coupon attivi</p>
            </div>
          </div>

          {/* Top Centri */}
          {advancedStats?.topTenantsByEntries && advancedStats.topTenantsByEntries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
              <p className="text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                Top centri per registrazioni:
              </p>
              <div className="space-y-2">
                {advancedStats.topTenantsByEntries.slice(0, 3).map((t, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-zinc-400 truncate">{t.tenantName}</span>
                    <span className="font-medium text-slate-800 dark:text-white">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to="/plans"
          className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-4 hover:shadow-md dark:hover:bg-zinc-800 transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-blue-100 dark:bg-violet-500/20 rounded-lg">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-violet-400" />
          </div>
          <span className="font-medium text-slate-800 dark:text-white">Gestisci Piani</span>
        </Link>
        <Link
          to="/coupons"
          className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-4 hover:shadow-md dark:hover:bg-zinc-800 transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-emerald-100 dark:bg-emerald-500/20 rounded-lg">
            <Euro className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <span className="font-medium text-slate-800 dark:text-white">Coupon</span>
        </Link>
        <Link
          to="/logs"
          className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-4 hover:shadow-md dark:hover:bg-zinc-800 transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-amber-100 dark:bg-amber-500/20 rounded-lg">
            <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <span className="font-medium text-slate-800 dark:text-white">Log Accessi</span>
        </Link>
        <Link
          to="/settings"
          className="bg-white dark:bg-zinc-900 rounded-lg dark:rounded-xl shadow-sm dark:shadow-none border border-slate-200 dark:border-zinc-800 p-4 hover:shadow-md dark:hover:bg-zinc-800 transition-all flex items-center gap-3"
        >
          <div className="p-2 bg-slate-100 dark:bg-zinc-700 rounded-lg">
            <Building2 className="h-5 w-5 text-slate-600 dark:text-zinc-400" />
          </div>
          <span className="font-medium text-slate-800 dark:text-white">Impostazioni</span>
        </Link>
      </div>
    </div>
  )
}
